'use client'

import React, { FunctionComponent } from 'react'
import { TranslationsProvider } from '@eo-locale/react'
import { getTwoLetterISOLanguageName, UserSettings } from '@dustin-web/microsite-config'

import ErrorBoundary from '../utils/error-boundary'
import { SiteContext, SiteContextProvider } from '../hooks/use-site-context'
import { UserSettingsContext } from '../hooks/use-user-settings'
import { CookiesContext } from '../hooks/use-cookie-state'
import Cookies from 'universal-cookie'
import { SimpleLayoutProvider } from '../hooks'
import { DataLoaderHooksProvider } from '@dustin-web/product-components/src/hooks/dataloader/use-dataloader-hooks'
import { DataLoaderHooks } from '@dustin-web/product-components/src/hooks/dataloader/data-loader-hooks-type'

let hasIntlErrors = false
const onIntlError = (intlError: any) => {
  if (!hasIntlErrors) {
    if (intlError.message.includes('[eo-locale] id missing')) {
      // eslint-disable-next-line no-console
      console.warn(intlError.message, 'Further intl errors will be suppressed.')
      hasIntlErrors = true

      return
    }

    throw intlError
  }
}

type RootProps = {
  siteContext: SiteContext
  userSettings: UserSettings
  messages: { [key: string]: string }
  children: React.ReactNode
}

const SimpleRoot: FunctionComponent<RootProps> = ({
  siteContext,
  userSettings,
  messages = {},
  children,
}) => {
  const twoLetterISOLanguageName = getTwoLetterISOLanguageName(userSettings.language)
  const dataLoaderHooks = {
    favorites: () => {},
    shoppingcart: () => {},
    compare: () => {},
    account: () => {},
    categoryNavigation: () => {},
    notifyAddToCart: () => {},
    notifyClick: () => {},
    search: () => {},
  }

  // We are only loading one language so no on-the-fly switching of locales allowed
  const locales = [
    {
      language: twoLetterISOLanguageName,
      messages,
    },
  ]

  const cookies = new Cookies()

  return (
    <ErrorBoundary>
      <SiteContextProvider value={siteContext}>
        <CookiesContext.Provider value={cookies.getAll()}>
          <UserSettingsContext.Provider value={userSettings}>
            <DataLoaderHooksProvider
              dataLoaderHooks={dataLoaderHooks as unknown as DataLoaderHooks}>
              <TranslationsProvider
                onError={onIntlError}
                language={twoLetterISOLanguageName}
                locales={locales}>
                <SimpleLayoutProvider>{children}</SimpleLayoutProvider>
              </TranslationsProvider>
            </DataLoaderHooksProvider>
          </UserSettingsContext.Provider>
        </CookiesContext.Provider>
      </SiteContextProvider>
    </ErrorBoundary>
  )
}

export default SimpleRoot
